import type { Maybe } from 'aem-libs-ts/src/types/utility'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { pipe } from 'aem-libs-ts/src/utils/common/pipe'

import { cookies } from './cookies'

const userIdCookie = '_ga'
const userIdParam = 'syncUserID'
const campaignParam = 'campaign'
const mediumParam = 'medium'
const georgeAppParam = 'georgeApp'
const stateParam = 'state'
const syncSessionIDParam = 'syncSessionID'
const sourcePageLocationParam = 'sourcePageLocation'
const domainParam = 'domain'

const triggerWebValue = 'web'

const cookieConsent = 'TC_PRIVACY_CENTER'
const cookieConsentTrue = '1'

const cookieCampaignId = 'csas_campaign_id'
const cookieSource = 'csas_source'
const cookieMedium = 'csas_medium'
const cookieSyncSessionID = '_ga_ZVX254WL4D'

const campaignCookieMap = {
  [cookieCampaignId]: 'id',
  [cookieSource]: 'source',
  [cookieMedium]: 'medium'
}

const cookiesConsentList = [
  {
    cookieName: 'client',
    cookieValue: 'csas_hashed_id'
  },
  {
    cookieName: 'activityUid',
    cookieValue: 'csas_activity_uid'
  },
  {
    cookieName: 'fbclid',
    cookieValue: 'csas_fbclid'
  },
  {
    cookieName: 'gclid',
    cookieValue: 'csas_gclid'
  },
  {
    cookieName: 'sznclid',
    cookieValue: 'csas_sznclid'
  },
  {
    cookieName: 'msclkid',
    cookieValue: 'csas_msclkid'
  },
  {
    cookieName: 'adformid',
    cookieValue: 'csas_adformid'
  }
]

const cookiesList = [
  {
    cookieName: 'utm_campaign',
    cookieValue: 'csas_campaign'
  },
  {
    cookieName: 'utm_medium',
    cookieValue: 'csas_medium'
  },
  {
    cookieName: 'utm_source',
    cookieValue: 'csas_source'
  },
  {
    cookieName: 'utm_id',
    cookieValue: 'csas_campaign_id'
  }
]

const createCampaignValue = () =>
  ([cookieCampaignId, cookieSource, cookieMedium] as const)
    .map((cookieKey) => ({
      cookieKey,
      value: cookies.getCookieByName(cookieKey)
    }))
    .filter((cookieValueMap) => isDefined(cookieValueMap.value))
    .map((filteredCookie) => `${campaignCookieMap[filteredCookie.cookieKey]}=${filteredCookie.value}`)
    .join(';')

export interface DeeplinkConfig {
  url: string
  fallbackPath?: Maybe<string>
  hash?: Maybe<string>
  medium?: Maybe<string>
}

export const createDeeplink = (deeplink: DeeplinkConfig, queryParams: URLSearchParams | null = null) => {
  const userId = cookies.getCookieByName(userIdCookie)?.match(/\d+\.\d+$/) ?? null
  const cookieConsentValue = cookies.getCookieByName(cookieConsent)
  const syncSessionID = cookies.getCookieByName(cookieSyncSessionID)?.match(/s(\d+)\$/) ?? null
  const sourcePageLocation = encodeURIComponent(window.location.origin + window.location.pathname)
  const domain = window.location.hostname.replace('www.', '')
  const fallback = deeplink.fallbackPath && deeplink.fallbackPath.length > 1 ? (deeplink.fallbackPath as string) : ''

  const campaignValue = createCampaignValue()
  const stateValue = queryParams?.get(stateParam)

  return pipe(() => new URL(deeplink.url + fallback))
    .tapWhen(
      () => Boolean(cookieConsentValue === cookieConsentTrue),
      (url) => {
        cookiesConsentList.forEach((cookie) => {
          const value = cookies.getCookieByName(cookie.cookieValue)
          if (value && value.length > 0) {
            url.searchParams.append(cookie.cookieName, value)
          }
        })
      }
    )
    .tap((url) => {
      url.searchParams.append(georgeAppParam, 'false')
      url.searchParams.append(sourcePageLocationParam, sourcePageLocation)
      url.searchParams.append(domainParam, domain)
      cookiesList.forEach((cookie) => {
        const value = cookies.getCookieByName(cookie.cookieValue)
        if (value && value.length > 0) {
          url.searchParams.append(cookie.cookieName, value)
        }
      })
    })
    .tapWhen(
      () => Boolean(userId && userId.length > 0 && cookieConsentValue === cookieConsentTrue),
      (url) => url.searchParams.append(userIdParam, userId as unknown as string)
    )
    .tapWhen(
      () => Boolean(syncSessionID && syncSessionID.length > 0),
      (url) => url.searchParams.append(syncSessionIDParam, syncSessionID?.[1] as unknown as string)
    )
    .tapWhen(
      (url) => campaignValue.length > 0 && !url.searchParams.get(campaignParam),
      (url) => {
        url.searchParams.append(campaignParam, campaignValue)
      }
    )
    .tapWhen(
      () => Boolean(deeplink.medium && deeplink.medium.length > 0),
      (url) => url.searchParams.append(mediumParam, deeplink.medium as string)
    )
    .tapWhen(
      () => Boolean(queryParams && queryParams.size > 0),
      (url) => {
        queryParams!.forEach((value, key) => url.searchParams.append(key, value))
      }
    )
    .tapWhen(
      () => !stateValue,
      (url) => url.searchParams.append(stateParam, btoa(JSON.stringify({ trigger: triggerWebValue })))
    )
    .tapWhen(
      () => Boolean(deeplink?.hash && deeplink.hash?.length > 0),
      (url) => (url.hash = deeplink.hash as string)
    )
    .map((url) => decodeURIComponent(url.toString()))
    .get()
}
